/**
 * In-app notification center domain models (issue #416).
 *
 * Mirrors the flymsg-node wire contract byte-for-byte (see
 * vengreso/flymsg-node#15, `src/features/notifications/types.ts` on branch
 * `issue-15-notification-system-backend-storage-publ`) — that file, not a
 * Postman collection, is the frozen interface both backends build against.
 * Consumed via {@link ApiResponse} generics per house style
 * (`dataService.get<ApiResponse<NotificationPage>>(...)`); flymsg-node uses
 * the identical `{result: ...}` envelope as flymsg-be.
 *
 * CONTRACT NOTE (post-QA-rejection on PR #512): `NotificationTemplate` is a
 * `{ kind, content: {...} }` **nested** shape, not a flat
 * `{ kind, title, body, ... }` shape. The first version of this file got
 * that wrong — a flat guess that matched nothing on the wire — and every
 * renderer silently rendered blank because `template.title` doesn't exist;
 * only `template.content.title` does. Re-verified directly against
 * `types.ts`'s `TemplateOf<K, C> = { kind: K; content: C }`, the admin
 * controller's `serializeCampaign` (`template: { kind: c.template_kind,
 * content: c.content }`), the admin create/edit handlers (which read
 * `body.template.content` when patching), AND a live API response captured
 * during QA: `{"template":{"kind":"text","content":{"title":"…","body":"…"}}}`.
 * `notification.model.spec.ts` pins a literal copy of that response as a
 * contract fixture so this can't silently drift again.
 */

/** Exact category enum from flymsg-node's `NOTIFICATION_CATEGORIES`. */
export type NotificationCategory =
  | "payments"
  | "invitations"
  | "rewards"
  | "marketing"
  | "sharing"
  | "promotions"
  | "gifts";

/** All known category values, in the order the filter tabs render them. */
export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "payments",
  "invitations",
  "rewards",
  "marketing",
  "sharing",
  "promotions",
  "gifts",
];

export interface NotificationActionOption {
  key: string;
  label: string;
  style?: "primary" | "secondary" | "danger";
}

export interface NotificationSurveyChoice {
  key: string;
  label: string;
}

export interface NotificationTextContent {
  title: string;
  body: string;
}

export interface NotificationLinkContent {
  title: string;
  body?: string;
  url: string;
  /** CTA button label, e.g. "Learn more". Defaults to "View" client-side. */
  label?: string;
}

export interface NotificationImageContent {
  title?: string;
  body?: string;
  image_url: string;
  alt?: string;
  /** Optional click-through, same as `NotificationLinkContent.url`. */
  url?: string;
}

export interface NotificationVideoContent {
  title?: string;
  body?: string;
  video_url: string;
  thumbnail_url?: string;
}

export interface NotificationActionsContent {
  title: string;
  body?: string;
  actions: NotificationActionOption[];
}

export interface NotificationSurveyContent {
  title: string;
  question: string;
  choices: NotificationSurveyChoice[];
  allow_multiple?: boolean;
}

interface TemplateOf<K extends string, C> {
  kind: K;
  content: C;
}

/**
 * Discriminated union on `kind`, matching flymsg-node's `TemplateOf<K, C>`
 * exactly: `content` is a NESTED object, not flattened onto the template.
 * Unknown/future kinds arrive typed as `TemplateOf<string, Record<string,
 * unknown>>` — the template registry (`notification-template-registry.ts`)
 * falls back to the `text` renderer for any kind it doesn't recognize, so a
 * widened `kind: string` here (not a closed literal union) keeps that
 * fallback path type-safe instead of requiring an `as any` cast at the one
 * place a new kind first arrives. A hypothetical new kind is expected to
 * still nest its payload under `content` (every kind does, including
 * unknown ones) — the text renderer's fallback reads `content.title`/
 * `content.body` if present, same as every known kind.
 */
export type NotificationTemplate =
  | TemplateOf<"text", NotificationTextContent>
  | TemplateOf<"link", NotificationLinkContent>
  | TemplateOf<"image", NotificationImageContent>
  | TemplateOf<"video", NotificationVideoContent>
  | TemplateOf<"actions", NotificationActionsContent>
  | TemplateOf<"survey", NotificationSurveyContent>
  | TemplateOf<string, Record<string, unknown>>;

export type NotificationSenderKind = "user" | "scheduled" | "company" | "cmc_admin";

export interface NotificationSender {
  kind: NotificationSenderKind;
  /** User id / company id / admin user id, depending on `kind`. Null for a `scheduled` sender with no human behind it. */
  id: string | null;
  /** Display name shown in the notification. */
  name: string | null;
}

/**
 * A single recipient's notification delivery (the `user_notifications` row,
 * not the authored campaign). `id` is the delivery id — every read/dismiss/
 * respond call addresses this id, not a campaign id.
 */
export interface AppNotification {
  id: string;
  category: NotificationCategory;
  template: NotificationTemplate;
  template_version: number;
  sender: NotificationSender;
  /** Epoch seconds (flymsg-node's envelope converts `Y-m-d H:i:s` on the way out); null if unknown. */
  created_at: number | null;
  read_at: number | null;
  dismissed_at: number | null;
  response: Record<string, unknown> | null;
}

/** `GET /api/v3/notifications` success payload. */
export interface NotificationPage {
  data: AppNotification[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

/** `GET /api/v3/notifications/unread-count` success payload. */
export interface NotificationUnreadCount {
  count: number;
}

/** `POST /api/v3/notifications/read-all` success payload. */
export interface NotificationMarkAllReadResult {
  updated: number;
}

/** `POST /api/v3/notifications/:id/dismiss` success payload. */
export interface NotificationDismissResult {
  success: boolean;
}

/** `POST /api/v3/notifications/:id/read` and `.../respond` success payload. */
export interface NotificationSingleResult {
  data: AppNotification;
}

/** Socket.IO event names on the flymsg-node `/notifications` namespace. */
export const NOTIFICATION_SOCKET_EVENTS = {
  NEW: "notification:new",
  UNREAD_COUNT: "notification:unread-count",
} as const;

/** `notification:unread-count` socket payload. */
export interface NotificationUnreadCountEvent {
  count: number;
}
